import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  useColorModeValue,
  Text,
  useToast,
  Card,
  CardBody,
  SimpleGrid,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { PrivateNavbar } from "../layout/PrivateNavbar";
import { Footer } from "../layout/Footer";
import { userAPI } from "../../services";
import { useAuthStore } from "../../stores/authStore";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export const Profile: React.FC = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { user, loadUser } = useAuthStore();
  const setUser = useAuthStore((state) => state.setUser);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cpf, setCpf] = useState(user?.cpf || "");
  console.log(user?.birthday);
  const [birthday, setBirthday] = useState<Date>(
    user?.birthday ? new Date(user.birthday) : new Date()
  );

  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const isSocialUser =
    (Boolean(user?.googleId) ||
      Boolean(user?.githubId) ||
      Boolean(user?.twitterId) ||
      Boolean(user?.facebookId)) &&
    !user?.password;

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  };

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formattedValue = formatCPF(value);
    setCpf(formattedValue.slice(0, 14));
  };

  const handleProfileUpdate = async () => {
    setIsLoading(true);
    try {
      const updatedUser = await userAPI.updateProfile({
        firstName: user?.firstName,
        lastName: user?.lastName,
        cpf,
        birthday: birthday.toISOString(),
      });
      setUser(updatedUser);
      toast({
        title: t("profile.update.success"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Error updating profile:", err);
      toast({
        title: t("profile.update.error"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError(t("profile.password.mismatch"));
      return;
    }

    if (newPassword.length < 8) {
      setError(t("profile.password.tooShort"));
      return;
    }

    setIsLoading(true);
    try {
      if (isSocialUser) {
        await userAPI.setPassword(newPassword);
      } else {
        await userAPI.updatePassword(currentPassword, newPassword);
      }

      toast({
        title: t("profile.password.success"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("Error updating password:", err);
      setError(t("profile.password.error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBirthdayChange = (date: Date | null) => {
    setBirthday(date || new Date());
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PrivateNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <Heading
              size="xl"
              fontFamily="Nasalization"
              bgGradient="linear(to-r, brand.primary, brand.secondary)"
              bgClip="text"
            >
              {t("profile.title")}
            </Heading>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              {/* User Info Card */}
              <Card bg={cardBg}>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <Heading size="md">{t("profile.info.title")}</Heading>
                    <FormControl>
                      <FormLabel>{t("profile.info.email")}</FormLabel>
                      <Input value={user?.email} isReadOnly />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t("profile.info.firstName")}</FormLabel>
                      <Input value={user?.firstName || "-"} isReadOnly />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t("profile.info.lastName")}</FormLabel>
                      <Input value={user?.lastName || "-"} isReadOnly />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t("profile.info.cpf")}</FormLabel>
                      <Input
                        value={cpf}
                        onChange={handleCPFChange}
                        placeholder="123.456.789-00"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t("profile.info.birthday")}</FormLabel>
                      <DatePicker
                        selected={birthday}
                        onChange={handleBirthdayChange}
                        dateFormat={
                          i18n.language === "pt-BR"
                            ? "dd/MM/yyyy"
                            : "MM/dd/yyyy"
                        }
                        placeholderText="Selecione a data de nascimento"
                      />
                    </FormControl>
                    <Button
                      colorScheme="brand"
                      onClick={handleProfileUpdate}
                      isLoading={isLoading}
                    >
                      {t("profile.info.update")}
                    </Button>
                  </VStack>
                </CardBody>
              </Card>

              {/* Password Update Card */}
              <Card bg={cardBg}>
                <CardBody>
                  <form onSubmit={handlePasswordUpdate}>
                    <VStack spacing={4} align="stretch">
                      <Heading size="md">
                        {isSocialUser
                          ? t("profile.password.setTitle")
                          : t("profile.password.title")}
                      </Heading>
                      {!isSocialUser && (
                        <FormControl>
                          <FormLabel>
                            {t("profile.password.currentPassword")}
                          </FormLabel>
                          <Input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required={!isSocialUser}
                          />
                        </FormControl>
                      )}
                      <FormControl isInvalid={!!error}>
                        <FormLabel>
                          {t("profile.password.newPassword")}
                        </FormLabel>
                        <Input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                        />
                      </FormControl>
                      <FormControl isInvalid={!!error}>
                        <FormLabel>
                          {t("profile.password.confirmPassword")}
                        </FormLabel>
                        <Input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                        />
                        {error && <FormErrorMessage>{error}</FormErrorMessage>}
                      </FormControl>
                      <Button
                        type="submit"
                        colorScheme="brand"
                        isLoading={isLoading}
                      >
                        {isSocialUser
                          ? t("profile.password.set")
                          : t("profile.password.update")}
                      </Button>
                    </VStack>
                  </form>
                </CardBody>
              </Card>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};
